import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Menu, X, Globe, User, LogOut, Compass, LayoutDashboard, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AVAILABLE_LANGUAGES } from '@/i18n';

// Custom Link that scrolls to top while preserving browser history
const ScrollLink = ({ to, children, className, ...props }) => {
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    // Use navigate with proper history handling
    navigate(to);
    // Scroll to top after navigation
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 0);
  };
  return <Link to={to} onClick={handleClick} className={className} {...props}>{children}</Link>;
};

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    // Save user's explicit language choice
    localStorage.setItem('xperience_language', langCode);
  };

  const currentLang = AVAILABLE_LANGUAGES.find(l => l.code === i18n.language) || AVAILABLE_LANGUAGES[0];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-orange-100 shadow-sm" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <ScrollLink to="/" className="flex items-center" data-testid="nav-logo">
            <span className="text-2xl font-bold">
              <span className="text-gray-600">X</span>
              <span className="text-primary font-serif">Perience</span>
            </span>
          </ScrollLink>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <ScrollLink 
              to="/explore" 
              className="text-gray-700 hover:text-primary transition-colors font-medium"
              data-testid="nav-explore"
            >
              {t('nav.explore')}
            </ScrollLink>
            
            {isAuthenticated && (
              <ScrollLink 
                to="/dashboard" 
                className="text-gray-700 hover:text-primary transition-colors font-medium"
                data-testid="nav-dashboard"
              >
                {t('nav.dashboard')}
              </ScrollLink>
            )}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 hover:bg-orange-100 transition-colors text-sm font-medium text-primary"
                  data-testid="lang-toggle"
                >
                  <span>{currentLang.flag}</span>
                  <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                  <Globe className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {AVAILABLE_LANGUAGES.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className="flex items-center justify-between cursor-pointer"
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="flex items-center gap-2">
                      <span>{lang.flag}</span>
                      <span>{lang.name}</span>
                    </span>
                    {i18n.language === lang.code && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth Buttons / User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user?.picture ? (
                      <img src={user.picture} alt={user.name} className="w-6 h-6 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="hidden sm:block text-sm font-medium">{user?.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="font-medium">{user?.name}</p>
                    <p className="text-sm text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive" data-testid="menu-logout">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('nav.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="nav-login"
                >
                  {t('nav.login')}
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="nav-register"
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <ScrollLink 
                to="/explore" 
                className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Compass className="w-5 h-5" />
                {t('nav.explore')}
              </ScrollLink>
              
              {isAuthenticated ? (
                <>
                  <ScrollLink 
                    to="/dashboard" 
                    className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    {t('nav.dashboard')}
                  </ScrollLink>
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary transition-colors text-destructive"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <>
                  <ScrollLink 
                    to="/login" 
                    className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-secondary transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <User className="w-5 h-5" />
                    {t('nav.login')}
                  </ScrollLink>
                  <ScrollLink 
                    to="/register" 
                    className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-accent text-accent-foreground"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.register')}
                  </ScrollLink>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
